import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { EllipsisV } from '@styled-icons/fa-solid/EllipsisV';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const ItemContainer = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-left: 3px solid transparent;
  background-color: ${props => (props.$isSelected && props.$hasMessages) ? 'var(--clr-bg-selected, #404040)' : 'transparent'};
  border-left-color: ${props => (props.$isSelected && props.$hasMessages) ? 'var(--clr-primary, #007bff)' : 'transparent'};
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    background-color: ${props => (props.$isSelected && props.$hasMessages) ? 'var(--clr-bg-selected, #404040)' : 'var(--clr-bg-hover, #333333)'};
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const UserInfoTop = styled.div`
  display: flex;
  align-items: center;
`;

const UserName = styled.div`
  font-weight: 600;
  color: ${props => props.$color || 'var(--clr-text, #ffffff)'};
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
`;

const OnlineIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$isOnline ? '#4caf50' : 'var(--clr-text-disabled, #666)'};
  margin-left: 6px;
  flex-shrink: 0;
`;

const RoomIndicator = styled.div`
  font-size: 0.75rem;
  color: var(--clr-text-secondary, #aaa);
  background-color: var(--clr-bg-tertiary, #1a1a1a);
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const BanBadge = styled.div`
  font-size: 0.7rem;
  color: #ff6b6b;
  background-color: rgba(255, 107, 107, 0.15);
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const LastActiveText = styled.div`
  color: var(--clr-text-secondary, #aaa);
  font-size: 0.75rem;
  margin-top: 0px;
  opacity: 0.9;
  white-space: nowrap;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const Timestamp = styled.div`
  font-size: 0.8rem;
  color: var(--clr-text-secondary, #aaa);
  white-space: nowrap;
`;

const UnreadBadge = styled.div`
  background-color: var(--clr-primary, #007bff);
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
`;

const LastMessage = styled.div`
  font-size: 0.85rem;
  color: var(--clr-text-secondary, #aaa);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-text-secondary, #aaa);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--clr-bg-hover, #3a3a3a);
    color: var(--clr-text, #ffffff);
  }
`;

const MenuIcon = styled(EllipsisV)`
  width: 16px;
  height: 16px;
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 16px;
  margin-top: 4px;
  background-color: var(--clr-bg-secondary, #2a2a2a);
  border: 1px solid var(--clr-border, #444);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 100;
  min-width: 150px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
`;

const MenuItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  color: var(--clr-text, #ffffff);
  cursor: pointer;
  padding: 10px 16px;
  text-align: left;
  font-size: 0.9rem;
  transition: background-color 0.2s ease;
  border-bottom: ${props => props.$hasSeparator ? '1px solid var(--clr-border, #444)' : 'none'};
  
  &:hover {
    background-color: var(--clr-bg-hover, #3a3a3a);
  }
  
  &:first-child {
    border-radius: 6px 6px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 6px 6px;
    border-bottom: none;
  }
  
  &.danger {
    color: #ff6b6b;
  }
`;

function DMConversationItem({
  conversation,
  friend,
  lastMessage,
  lastMessageAt,
  unreadCount = 0,
  isSelected = false,
  onClick,
  onRemoveFriend,
  onViewProfile,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Use friend data as the primary source
  const displayName = friend?.nickname || 'Anon';
  const userColor = friend?.custom_color || friend?.assigned_color;
  const isOnline = friend?.isOnline;
  const currentRoom = friend?.currentRoomTitle;
  const lastActiveAt = friend?.last_active_at;
  const lastActiveRoom = friend?.last_active_room_name;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    } else if (diffInMinutes < 1440) { // 24 hours
      return `${Math.floor(diffInMinutes / 60)}h`;
    } else {
      const diffInDays = Math.floor(diffInMinutes / 1440);
      return `${diffInDays}d`;
    }
  };

  const formatLastMessage = (message) => {
    if (!message) return 'Click to start chatting';
    
    const prefix = message.isFromMe ? 'You: ' : '';
    const text = message.messageText.length > 35 
      ? message.messageText.substring(0, 35) + '...'
      : message.messageText;
    
    return prefix + text;
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleRemoveFriend = (e) => {
    e.stopPropagation();
    if (confirmingRemove) {
      // Actually remove the friend
      setMenuOpen(false);
      setConfirmingRemove(false);
      onRemoveFriend();
    } else {
      // Show confirmation
      setConfirmingRemove(true);
    }
  };

  const handleCancelRemove = (e) => {
    e.stopPropagation();
    setConfirmingRemove(false);
  };

  const handleViewProfile = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onViewProfile?.(friend.id);
  };

  const hasMessages = !!lastMessage;

  return (
    <ItemContainer $isSelected={isSelected} $hasMessages={hasMessages} onClick={onClick}>
      <ItemHeader>
        <UserInfo>
          <UserInfoTop>
            <UserName $color={userColor}>
              {displayName}
            </UserName>
            <OnlineIndicator $isOnline={isOnline} />
            {isOnline && currentRoom && (
              <RoomIndicator>
                {currentRoom}
              </RoomIndicator>
            )}
            {friend?.isBanned && (
              <BanBadge>
                Banned{friend.unbanDate ? ` until ${dayjs(friend.unbanDate).format('MMM D, YYYY')}` : ''}
              </BanBadge>
            )}
          </UserInfoTop>
          {lastActiveAt && (
            <LastActiveText>
              Last active: {dayjs(lastActiveAt).fromNow()}
              {lastActiveRoom && ` in ${lastActiveRoom}`}
            </LastActiveText>
          )}
        </UserInfo>
        
        <ItemMeta>
          {lastMessageAt && (
            <Timestamp>
              {formatTimestamp(lastMessageAt)}
            </Timestamp>
          )}
          
          {unreadCount > 0 && (
            <UnreadBadge>
              {unreadCount}
            </UnreadBadge>
          )}
          
          <MenuButton ref={buttonRef} onClick={handleMenuClick}>
            <MenuIcon />
          </MenuButton>
        </ItemMeta>
      </ItemHeader>
      
      <LastMessage>
        {formatLastMessage(lastMessage)}
      </LastMessage>
      
      <MenuDropdown ref={menuRef} $isOpen={menuOpen}>
        {confirmingRemove ? (
          <>
            <MenuItem className="danger" onClick={handleRemoveFriend} $hasSeparator>
              Confirm Remove
            </MenuItem>
            <MenuItem onClick={handleCancelRemove}>
              Cancel
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={handleViewProfile} $hasSeparator>
              👤 View Profile
            </MenuItem>
            <MenuItem className="danger" onClick={handleRemoveFriend}>
              Remove Friend
            </MenuItem>
          </>
        )}
      </MenuDropdown>
    </ItemContainer>
  );
}

export default DMConversationItem;